// Main script

// panels — reusable sliding drawer system
// trigger: element or selector for the element that opens/closes the panel
// panel: element or selector for the panel element
// options:
//   withOverlay: bool — show a clickable overlay behind the panel (default: true)
//   closeBtn: selector — dedicated close element inside the panel (default: ".close-btn")
//   withHeaderPadding: bool — set padding-top: var(--header-height) on the panel (default: true)
function initPanel(trigger, panel, options) {
  const defaults = {
    withOverlay: true,
    closeBtn: '.close-btn',
    withHeaderPadding: true,
  }
  const opts = Object.assign({}, defaults, options)
  const triggerEl =
    typeof trigger === 'string' ? document.querySelector(trigger) : trigger
  const panelEl =
    typeof panel === 'string' ? document.querySelector(panel) : panel

  if (!triggerEl || !panelEl) return

  if (opts.withHeaderPadding) {
    panelEl.style.paddingTop = 'var(--header-height)'
  }

  // clean up previous bindings before re-init (breakpoint change)
  if (triggerEl._panelAbort) triggerEl._panelAbort.abort()
  const controller = new AbortController()
  triggerEl._panelAbort = controller
  const { signal } = controller

  function openPanel() {
    document.dispatchEvent(
      new CustomEvent('panel:closeOthers', { detail: { except: panelEl } })
    )
    panelEl.classList.add('open')
    triggerEl.classList.add('open')
    document.body.classList.add('panel-open')
    if (opts.withOverlay) {
      if (!document.getElementById('panel-overlay')) {
        document.body.insertAdjacentHTML(
          'beforeend',
          '<div id="panel-overlay"></div>'
        )
      }
      const overlay = document.getElementById('panel-overlay')
      overlay.classList.add('visible')
      overlay.addEventListener('click', closePanel, { once: true })
    }
  }

  function closePanel() {
    panelEl.classList.remove('open')
    triggerEl.classList.remove('open')
    document.body.classList.remove('panel-open')
    if (opts.withOverlay) {
      document.getElementById('panel-overlay')?.classList.remove('visible')
    }
  }

  document.addEventListener(
    'panel:closeOthers',
    (e) => {
      if (panelEl !== e.detail.except && panelEl.classList.contains('open')) {
        closePanel()
      }
    },
    { signal }
  )

  triggerEl.addEventListener(
    'click',
    (e) => {
      e.preventDefault()
      panelEl.classList.contains('open') ? closePanel() : openPanel()
    },
    { signal }
  )

  if (opts.closeBtn) {
    panelEl.querySelectorAll(opts.closeBtn).forEach((btn) => {
      btn.addEventListener('click', closePanel, { signal })
    })
  }
}

// panels — auto-init from data attributes on .panel-btn elements
// data-panel: target panel selector (fallback: href)
// data-with-overlay: bool
// data-close-btn: selector
// data-with-header-padding: bool
// function initPanels() {
//   document.querySelectorAll('.panel-btn').forEach((btn) => {
//     const target = btn.dataset.panel || btn.getAttribute('href')
//     const opts = {}
//     if (btn.dataset.withOverlay !== undefined)
//       opts.withOverlay = btn.dataset.withOverlay === 'true'
//     if (btn.dataset.closeBtn !== undefined) opts.closeBtn = btn.dataset.closeBtn
//     if (btn.dataset.withHeaderPadding !== undefined)
//       opts.withHeaderPadding = btn.dataset.withHeaderPadding === 'true'
//     initPanel(btn, target, opts)
//   })
// }

// hover menus — desktop only
// opens sub-menus on mouseenter with a short delay to avoid accidental triggers
// closes immediately on mouseleave
// signal: AbortSignal — tied to the shell lifecycle, cleaned up on breakpoint change
function initHoverMenus(signal) {
  const menuItems = document.querySelectorAll('#menu-container .menu-top-level')

  menuItems.forEach((item) => {
    const toggle = item.querySelector(':scope > .toggle')
    const submenu = item.querySelector(':scope > .sub-menu-container')
    if (!toggle || !submenu) return

    let hoverTimer

    item.addEventListener(
      'mouseenter',
      () => {
        hoverTimer = setTimeout(() => {
          menuItems.forEach((other) => {
            if (other !== item) {
              other
                .querySelector(':scope > .toggle')
                ?.classList.remove('active')
              other
                .querySelector(':scope > .toggle')
                ?.setAttribute('aria-expanded', 'false')
              other
                .querySelector(':scope > .sub-menu-container')
                ?.classList.remove('active')
            }
          })
          toggle.classList.add('active')
          toggle.setAttribute('aria-expanded', 'true')
          submenu.classList.add('active')
        }, 250)
      },
      { signal }
    )

    item.addEventListener(
      'mouseleave',
      () => {
        clearTimeout(hoverTimer)
        toggle.classList.remove('active')
        toggle.setAttribute('aria-expanded', 'false')
        submenu.classList.remove('active')
      },
      { signal }
    )
  })
}

// shell
let shellController = null

function initShell(mobile) {
  // clean up previous shell bindings on breakpoint change
  if (shellController) shellController.abort()
  shellController = new AbortController()
  const { signal } = shellController

  const header = document.getElementById('header')
  const main = document.querySelector('main')
  const menuContainer = document.getElementById('menu-container')
  const logo = document.getElementById('logo')

  if (!menuContainer) return

  if (header) {
    // Header offset is not as expected when loading JS.
    // Therefore we've better compute offset from existing sub-elements.
    const headerHeight =
      (document.getElementById('env-banner')?.offsetHeight || 0) +
      (document.getElementById('pre-header')?.offsetHeight || 0) +
      (document.getElementById('header-heart')?.offsetHeight || 0)
    main.style.paddingTop = headerHeight + 'px'
    document.documentElement.style.setProperty(
      '--header-height',
      headerHeight + 'px'
    )
  }

  if (mobile) {
    if (header) {
      header.insertAdjacentElement('afterend', menuContainer)
      menuContainer.style.paddingTop = header.offsetHeight + 'px'
      const btn = document.getElementById('mobile-user-btn')
      if (btn) {
        menuContainer.style.paddingBottom = btn.offsetHeight - 1 + 'px'
      }
    }
    initPanel('.menu-btn', '#menu-container', { withOverlay: false })
  } else {
    logo?.insertAdjacentElement('afterend', menuContainer)
    menuContainer.style.paddingTop = '0'
    // disable mobile menu panel behavior
    const menuBtn = document.querySelector('.menu-btn')
    if (menuBtn?._panelAbort) menuBtn._panelAbort.abort()
    // hover menus on desktop
    initHoverMenus(signal)
  }

  // open/close sub-menus on click
  // mobile: primary interaction / desktop: accessible fallback alongside hover
  // document
  //   .querySelectorAll('#menu-container .menu-top-level > .toggle')
  //   .forEach((toggle) => {
  //     toggle.addEventListener(
  //       'click',
  //       function (e) {
  //         e.preventDefault()

  //         document
  //           .querySelectorAll('#menu-container .menu-top-level > .toggle')
  //           .forEach((other) => {
  //             if (other !== toggle) {
  //               other.setAttribute('aria-expanded', 'false')
  //               other.classList.remove('active')
  //               other.nextElementSibling?.classList.remove('active')
  //             }
  //           })
  //         const isOpen = toggle.classList.toggle('active')
  //         toggle.nextElementSibling?.classList.toggle('active')
  //         toggle.setAttribute('aria-expanded', isOpen ? 'true' : 'false')
  //       },
  //       { signal }
  //     )
  //   })
}

// sticky nav
function stickyNav() {
  const header = document.getElementById('header')
  window.addEventListener('scroll', () => {
    if (header && window.scrollY >= header.offsetHeight) {
      header.classList.add('sticky')
      document
        .querySelectorAll('.main-nav .sub-menu-container')
        .forEach((el) => {
          el.classList.remove('active')
        })
    } else {
      header.classList.remove('sticky')
    }
  })
}

// hidden input filter — toggles .active on the label when a hidden checkbox/radio changes
// works with <label for="id"> and <label><input> patterns
// usable for any filter type: size, color, material, etc.
function initHiddenInputFilter() {
  document.querySelectorAll('.hidden-input-filter input').forEach((input) => {
    const label =
      document.querySelector(`label[for="${input.id}"]`) ||
      input.closest('label')
    if (!label) return
    // sync active class on init (in case of pre-checked state)
    label.classList.toggle('active', input.checked)
    input.addEventListener('change', () => {
      label.classList.toggle('active', input.checked)
    })
  })
}

// accordions
function initAccordions() {
  document.querySelectorAll('.accordion dt:not(.active)').forEach((dt) => {
    dt.addEventListener('click', function () {
      this.classList.toggle('open')
      this.setAttribute(
        'aria-expanded',
        this.getAttribute('aria-expanded') === 'true' ? 'false' : 'true'
      )
    })
  })
}

// RWD — re-init shell on breakpoint change (mobile: < 768px)
const mobileQuery = window.matchMedia('(max-width: 767px)')
mobileQuery.addEventListener('change', (e) => initShell(e.matches ? 1 : 0))

// Make whole card clickable
function bindProductThumbWholeLink() {
  document.querySelectorAll('.card').forEach((card) => {
    card.addEventListener('click', (e) => {
      e.stopPropagation()
      e.preventDefault()

      const link = card.querySelector('a')
      if (!link) return

      const url = link.getAttribute('href')
      if (!url) return

      if (link.getAttribute('target') === '_blank') {
        window.open(url, '_blank')
        return
      }
      window.location.href = url
    })
  })
}

// Init
document.addEventListener('DOMContentLoaded', () => {
  initShell(mobileQuery.matches ? 1 : 0)
  stickyNav()
  // initPanels()
  initAccordions()
  initHiddenInputFilter()
  bindProductThumbWholeLink()
})
